import * as ko from "knockout";
import * as localisationHelper from "localisationHelper";

// uidExtender and bindings
import "@legend/knockout/binding/id";
import "@legend/knockout/extender/uid";
import type { ObservableWithUid } from "@legend/knockout/extender/uid";

// Helpers
import "readOnlyDisabled";
import "knockout.validation";
import "knockoutCustomBindings";
import "tooltipPopoverBindings";

// Services
import { ValidationService } from "Content/ts/Shared/Services/Validation/validationService";

// Components
import "Content/ts/Shared/BootstrapAlerts/AlertValidation/alert-validation";
import { InputsBaseValidationMessages } from "Content/ts/Shared/Inputs/InputsBaseValidationMessages";

export interface IInputsBase {
    Value: ObservableWithUid<any>;                                                      // Required

    MaxLength: number;                                                                  // Optional
    IsDisabled: boolean | KnockoutObservable<boolean>;                                  // Optional
    IsRequired: boolean | KnockoutObservable<boolean>;                                  // Optional
    IsReadOnly: boolean | KnockoutObservable<boolean>;                                  // Optional
    TooltipMessage: string | KnockoutObservable<string>;                                // Optional
    OnFocus: () => void;
    OnChange: (value?: any) => void;                                                   // Optional

    ValidationFields: KnockoutObservable<any>[];                                        // Conditional - Some components may need this in order to function
    Text: string | KnockoutObservable<string>;                                          // Conditional - Some components may need this in order to function
    Placeholder: string | KnockoutObservable<string>;                                   // Conditional - Some components may need this in order to function

    AriaDescribedById: string | KnockoutObservable<string>;

    ValidationMessages: InputsBaseValidationMessages;
}

export default class InputsBase {
    value: ObservableWithUid<any>;
    validationFields: KnockoutObservable<any>[];
    maxLength: number;
    text: string | KnockoutObservable<string>;
    Placeholder: string | KnockoutObservable<string>;
    IsDisabled: boolean | KnockoutObservable<boolean>;
    IsReadOnly: boolean | KnockoutObservable<boolean>;
    isRequired: boolean | KnockoutObservable<boolean>;
    TooltipMessage: string | KnockoutObservable<string>;

    onFocus: () => void;
    onChange: (value?: any) => void | Promise<boolean>;

    HasTooltipMessage: KnockoutComputed<boolean>;

    ariaDescribedById: string | KnockoutObservable<string>;

    ValidationMessages: InputsBaseValidationMessages;

    subscriptions: KnockoutSubscription[];
    constructor(params: IInputsBase) {
        // Provided Component Properties
        this.text = params.Text;
        this.value = params.Value;
        this.maxLength = params.MaxLength;
        this.Placeholder = params.Placeholder;
        this.TooltipMessage = params.TooltipMessage;
        this.isRequired = params.IsRequired || false;
        this.IsDisabled = params.IsDisabled || false;
        this.IsReadOnly = params.IsReadOnly || false;
        this.subscriptions = [];
        this.ariaDescribedById = params.AriaDescribedById || "";

        this.onFocus = params.OnFocus || function (): void { };
        this.onChange = params.OnChange || function (): void { };

        this.validationFields = params.ValidationFields || [];

        // if observable doesn't already have uid add one using target class name as the prefix
        if (!this.value.uid) {
            this.value.extend({ uid: new.target.name });
        }

        // Initialisation Methods
        this.ComputedMethods();
        this.SetupValidation(params.ValidationMessages);
        this.SetupSubscriptions();
    }

    private SetupSubscriptions() {
        this.subscriptions.push(
            this.value.subscribe(() => {
                this.HandleChange();
            }));
    }

    protected HandleChange() {
        this.onChange(this.value());
    }

    private ComputedMethods(): void {
        this.HasTooltipMessage = ko.pureComputed(() => {
            return this.TooltipMessage != null && this.TooltipMessage != undefined;
        });
    }

    protected SetupValidation(validationMsgs: InputsBaseValidationMessages): void {
        const requiredValidationMsg = validationMsgs?.Required
            || localisationHelper.getString("ValidationMessageResources", "FieldIsRequired")
                .replace("{name}", ko.unwrap(this.text));

        ValidationService.AddValidationRule(this.value, this.validationFields, {
            required: {
                params: true,
                message: requiredValidationMsg,
                onlyIf: () => {
                    return ko.unwrap(this.isRequired);
                }
            }
        });
    }

    private removeValidation(): void {
        ValidationService.RemoveValidationRule(this.value, this.validationFields);
    }

    public dispose(): void {
        this.removeValidation();
        this.subscriptions.forEach((sub: KnockoutSubscription) => sub.dispose());
    }
}

const inputTextComponentName = "input-text";
ko.components.unregister(inputTextComponentName);
ko.components.register(inputTextComponentName, {
    viewModel: InputsBase,
    template: { require: `text!Content/ts/Shared/Inputs/Text/${inputTextComponentName}.html` }
});

const inputTextLiveComponentName = "input-text-live";
ko.components.unregister(inputTextLiveComponentName);
ko.components.register(inputTextLiveComponentName, {
    viewModel: InputsBase,
    template: { require: `text!Content/ts/Shared/Inputs/Text/${inputTextLiveComponentName}.html` }
});

const inputTextAreaComponentName = "input-textarea";
ko.components.unregister(inputTextAreaComponentName);
ko.components.register(inputTextAreaComponentName, {
    viewModel: InputsBase,
    template: { require: `text!Content/ts/Shared/Inputs/TextArea/${inputTextAreaComponentName}.html` }
});

const inputPasswordComponentName = "input-password";
ko.components.unregister(inputPasswordComponentName);
ko.components.register(inputPasswordComponentName, {
    viewModel: InputsBase,
    template: { require: `text!Content/ts/Shared/Inputs/Password/${inputPasswordComponentName}.html` }
});